import { useEffect, useState } from "react";
import { ModalShell } from "../ModalShell";
import { getCsrfHeader } from "../../lib/csrf";
import { useCachedQuery } from "../../hooks/useCachedQuery";
import dialogStyles from "../../pages/admin/AdminFeatureFlags.module.css";
import styles from "./RestartAgentModal.module.css";
import {
  POLL_INTERVAL_MS,
  fetchLatestRun,
  recoveryCopy,
  shownRun,
  type RecoveryRun,
} from "./restartRecovery";

type Props = {
  tenantId: string;
  instanceName: string;
  onClose: () => void;
};

// A touch wider than the default "sm" preset (440px) so the body copy sits
// comfortably without an awkward wrap.
const MODAL_WIDTH = "min(480px, 92vw)";

const GENERIC_ERROR = "We couldn't restart your agent just now. Please try again in a minute.";

// Self-serve "Restart agent". The backend restarts the gateway in the
// background, rebooting the server if that fails; this modal polls the run.
// Reopening it mid-run shows the run in progress. Owner/manager only.
export const RestartAgentModal = ({ tenantId, instanceName, onClose }: Props): JSX.Element => {
  const [started, setStarted] = useState<RecoveryRun | null>(null);
  const [pollMs, setPollMs] = useState(0);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const titleId = "restart-agent-dialog-title";
  const url = `/api/claws/${encodeURIComponent(tenantId)}/gateway-restart`;

  const latest = useCachedQuery<RecoveryRun | null>({
    cacheResource: null,
    queryKey: url,
    fetcher: (signal) => fetchLatestRun(url, signal),
    refreshIntervalMs: pollMs,
  });
  const run = shownRun(latest.data, started);
  const inFlight = run !== null && run.stage !== "done";

  useEffect(() => {
    setPollMs(inFlight ? POLL_INTERVAL_MS : 0);
  }, [inFlight]);

  const restart = async (): Promise<void> => {
    setError(null);
    setStarting(true);
    try {
      const res = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { ...getCsrfHeader() },
      });
      if (res.status === 202) {
        setStarted((await res.json()) as RecoveryRun);
        return;
      }
      if (res.status === 429) {
        setError("Your agent was just restarted. Give it a moment, then try again.");
      } else if (res.status === 409) {
        const body = (await res.json().catch(() => null)) as { message?: string } | null;
        setError(body?.message ?? "Your agent can't be restarted right now.");
      } else if (res.status === 401) {
        setError("Your session expired. Refresh the page and try again.");
      } else {
        const body = (await res.json().catch(() => null)) as { message?: string } | null;
        setError(body?.message ?? GENERIC_ERROR);
      }
    } catch {
      setError(GENERIC_ERROR);
    } finally {
      setStarting(false);
    }
  };

  if (run !== null) {
    const copy = recoveryCopy(run, instanceName);
    return (
      <ModalShell onClose={onClose} titleId={titleId} size="sm" width={MODAL_WIDTH}>
        <header className={dialogStyles.dialogHeader}>
          <h3 className={dialogStyles.dialogTitle} id={titleId}>
            {copy.title}
          </h3>
          <button type="button" className={dialogStyles.closeBtn} onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>
        <p
          className={styles.body}
          role="status"
          aria-live="polite"
          data-stage={run.stage}
          data-outcome={run.outcome ?? ""}
        >
          {copy.body}
        </p>
        <footer className={dialogStyles.footer}>
          <button type="button" className={styles.primaryBtn} onClick={onClose} autoFocus>
            {copy.finished ? "Done" : "Close"}
          </button>
        </footer>
      </ModalShell>
    );
  }

  return (
    <ModalShell onClose={starting ? () => undefined : onClose} titleId={titleId} size="sm" width={MODAL_WIDTH}>
      <header className={dialogStyles.dialogHeader}>
        <h3 className={dialogStyles.dialogTitle} id={titleId}>
          Restart your agent?
        </h3>
        <button
          type="button"
          className={dialogStyles.closeBtn}
          onClick={onClose}
          aria-label="Close"
          disabled={starting}
        >
          ×
        </button>
      </header>
      <p className={styles.body}>
        If <strong>{instanceName}</strong> has stopped replying (on WhatsApp, Telegram, or chat) or
        seems stuck, restarting it usually gets it going again. It'll be offline for a minute or two,
        or a few minutes if we also need to restart its server. Your conversations, settings, and
        connections are all kept.
      </p>
      {error !== null ? <p className={dialogStyles.errorBanner}>{error}</p> : null}
      <footer className={dialogStyles.footer}>
        <button type="button" className={styles.secondaryBtn} onClick={onClose} disabled={starting}>
          Cancel
        </button>
        <button type="button" className={styles.primaryBtn} onClick={() => void restart()} disabled={starting}>
          {starting ? "Restarting…" : "Restart agent"}
        </button>
      </footer>
    </ModalShell>
  );
};
